/**
 * @file: facadeSiteTagOptions.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { AnnotationTagOption } from '@euler/app/flows/media/annotation/functions/AnnotationTagOption';
import { InspectionItemOptionInfo, InspectionSiteInfo } from '@euler/model';
import { getTargetFacadeSiteOptionInfo } from './isRecordOnlySite';

/**
 * The result options the photo tags of a facade site can stand for, so that
 * each photo carries its own result and the site takes the most defective of
 * them.
 *
 * A site offering a single result — a record-only site — has nothing to pick
 * from, hence no options.
 */
export const facadeSiteTagOptions = (
  site: InspectionSiteInfo,
): AnnotationTagOption[] => {
  const options = site.checkItems[0]?.options ?? [];
  return options.length > 1 ? options.map(toTagOption) : [];
};

/**
 * The result the tag placed automatically on a facade photo stands for, i.e.
 * the result the site takes when the technician picks nothing. Undefined for
 * a site whose result is not selectable in the first place.
 */
export const facadeDefaultTagOption = (
  site: InspectionSiteInfo,
): AnnotationTagOption | undefined => {
  if (!facadeSiteTagOptions(site).length) return undefined;
  const { option } = getTargetFacadeSiteOptionInfo(site);
  return option ? toTagOption(option) : undefined;
};

const toTagOption = (
  option: InspectionItemOptionInfo,
): AnnotationTagOption => ({
  id: option.id,
  label: option.label,
  abnormalLevel: option.abnormalLevel,
  severityLevel: option.severityLevel,
});
